import { Component, Input, OnInit } from "@angular/core";
import { CostComparison } from "../../models/cost-comparison/cost-comparison.model";
import { Proposal, SummaryCalculationMode } from "../../models/proposal/proposal.model";
import { FeesSummaryService } from "../../core/fees-summary-service/fees-summary.service";

@Component({
    selector: 'fees-summary-comparison',
    templateUrl: './fees-summary-comparison.component.html',
    styleUrls: ['./fees-summary-comparison.component.scss'],
    standalone: false
})
export class FeesSummaryComparisonComponent implements OnInit {
    private _costComparisonObjects: Array<CostComparison> = [];
    protected readonly Math = Math;
    processorFees: Array<any> = [];
    costComparisonCount: number = 0;
    statementsToDisplay: string[] = [];

    @Input() proposal!: Proposal;

    @Input()
    set costComparisonObjects(display: Array<CostComparison>) {
        this._costComparisonObjects = display;
        this.fixFees();
    }

    get costComparisonObjects(): Array<CostComparison> {
        return this._costComparisonObjects;
    }

    constructor(private feesSummaryService: FeesSummaryService) { }

    ngOnInit(): void {
        this._costComparisonObjects.forEach(cc => {
            this.statementsToDisplay.push(cc?.statementData?.[0]?.statementName ?? '');
        });
        this.costComparisonCount = this._costComparisonObjects.length;
    }

    get isMedian(): boolean {
        return this.proposal?.summaryCalculationMode === SummaryCalculationMode.median;
    }

    get calculationModeLabel(): string {
        return this.isMedian ? 'MEDIAN' : 'AVERAGE';
    }

    get summaryBasisLabel(): string {
        const monthYear = this.proposal?.summaryMonthYear;
        const hasMonthYearData = monthYear?.length && monthYear.some(my => my.month || my.year);

        if (hasMonthYearData) {
            return monthYear
                .filter(my => my.month || my.year)
                .map(my => [my.month, my.year].filter(Boolean).join(' '))
                .join(' and ');
        }

        return `${this.getCostComparisonCountInWords()} monthly statements`;
    }

    fixFees() {
        this.processorFees = this.feesSummaryService.fixFees(this.costComparisonObjects);
        return this.processorFees;
    }

    getSummaryAverageCurrentTotalMonthlyFees() {
        return this.feesSummaryService.getSummaryAverageCurrentTotalMonthlyFees(this.costComparisonObjects);
    }

    getCurrentTotalMonthlyFeesSummary(ccObj: CostComparison) {
        return this.feesSummaryService.getCurrentTotalMonthlyFeesSummary(ccObj);
    }

    getCurrentEffectiveRateSummary() {
        return this.feesSummaryService.getCurrentEffectiveRateSummary(this.costComparisonObjects);
    }

    getProposedTotalMonthlyFeesSummary(ccObj: CostComparison) {
        return this.feesSummaryService.getProposedTotalMonthlyFeesSummary(ccObj);
    }

    getProposedEffectiveRateSummary() {
        return this.feesSummaryService.getProposedEffectiveRateSummary(this.costComparisonObjects);
    }

    getSummaryAverageProposedTotalFees() {
        return this.feesSummaryService.getSummaryAverageProposedTotalFees(this.costComparisonObjects);
    }

    getSavingsTotalMonthlyFees() {
        return this.feesSummaryService.getSavingsTotalMonthlyFees(this.costComparisonObjects);
    }

    getCurrentTotalFees() {
        let currentTotalFees = 0;
        this.costComparisonObjects.forEach(ccObj => {
            const fg = ccObj?.comparisonFeeGroupings;
            if (!fg) {
                return;
            }
            currentTotalFees +=
                fg.processor.totalAmount +
                fg.additional.totalAmount +
                fg.assessment.totalAmount +
                fg.nonNegotiable.totalAmount +
                fg.interchange.totalAmount;
        });
        return currentTotalFees;
    }

    getSummaryMedianCurrentTotalMonthlyFees() {
        return this.feesSummaryService.getSummaryMedianCurrentTotalMonthlyFees(this.costComparisonObjects);
    }

    getSummaryMedianProposedTotalFees() {
        return this.feesSummaryService.getSummaryMedianProposedTotalFees(this.costComparisonObjects);
    }

    getCurrentEffectiveRateMedian() {
        return this.feesSummaryService.getCurrentEffectiveRateMedian(this.costComparisonObjects);
    }

    getProposedEffectiveRateMedian() {
        return this.feesSummaryService.getProposedEffectiveRateMedian(this.costComparisonObjects);
    }

    getSavingsTotalMonthlyFeesMedian() {
        return this.feesSummaryService.getSavingsTotalMonthlyFeesMedian(this.costComparisonObjects);
    }

    getProposedTotalFees() {
        let proposedTotalFees = 0;
        this.costComparisonObjects.forEach(ccObj => {
            const fg = ccObj?.comparisonFeeGroupings;
            if (!fg) {
                return;
            }
            proposedTotalFees +=
                fg.processor.proposedTotalAmount +
                fg.additional.proposedTotalAmount +
                fg.assessment.proposedTotalAmount +
                fg.nonNegotiable.proposedTotalAmount +
                fg.interchange.proposedTotalAmount;
        });
        return proposedTotalFees;
    }

    getTotalMonthlySavings() {
        let savingsTotalMonthlyFees = 0;
        this.costComparisonObjects.forEach(ccObj => {
            const fg = ccObj?.comparisonFeeGroupings;
            if (!fg) {
                return;
            }
            savingsTotalMonthlyFees +=
                fg.processor.totalAmount - fg.processor.proposedTotalAmount +
                fg.additional.totalAmount - fg.additional.proposedTotalAmount +
                fg.assessment.totalAmount - fg.assessment.proposedTotalAmount +
                fg.nonNegotiable.totalAmount - fg.nonNegotiable.proposedTotalAmount +
                fg.interchange.totalAmount - fg.interchange.proposedTotalAmount;
        });
        return savingsTotalMonthlyFees;
    }

    getCostComparisonCountInWords(): string {
        const words = [
            'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
            'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'
        ];
        return words[this.costComparisonCount] ?? this.costComparisonCount.toString();
    }
}